import React, { useEffect, useMemo, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { useTranslation } from "react-i18next";
import "react-easy-crop/react-easy-crop.css";
import { useDropzone } from "react-dropzone";
import { Plus, Upload, X, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../dialog";

export type ImageUploadCropperProps = {
  value?: File | string | null;
  onChange: (file: File | null) => void;
  aspect?: number;
  title?: string;
  description?: string;
  disabled?: boolean;
  className?: string;
  previewClassName?: string;
  emptyClassName?: string;
  previewShape?: "rect" | "round";
  error?: string;
  accept?: Record<string, string[]>;
  cropShape?: "rect" | "round";
  maxZoom?: number;
  minZoom?: number;
  outputFileName?: string;
};

const createImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", reject);
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getRadianAngle = (degreeValue: number) => (degreeValue * Math.PI) / 180;

const rotateSize = (width: number, height: number, rotation: number) => {
  const rotRad = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
};

async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  rotation = 0,
  cropShape: "rect" | "round" = "rect",
  fileName: string,
): Promise<File> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Canvas context is not available");
  }

  const rotRad = getRadianAngle(rotation);
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);
  const devicePixelRatio = window.devicePixelRatio || 1;

  canvas.width = Math.floor(bBoxWidth * devicePixelRatio);
  canvas.height = Math.floor(bBoxHeight * devicePixelRatio);

  ctx.scale(devicePixelRatio, devicePixelRatio);
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement("canvas");
  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) {
    throw new Error("Canvas crop context is not available");
  }

  croppedCanvas.width = Math.floor(pixelCrop.width * devicePixelRatio);
  croppedCanvas.height = Math.floor(pixelCrop.height * devicePixelRatio);
  croppedCtx.scale(devicePixelRatio, devicePixelRatio);

  if (cropShape === "round") {
    croppedCtx.beginPath();
    croppedCtx.arc(
      pixelCrop.width / 2,
      pixelCrop.height / 2,
      Math.min(pixelCrop.width, pixelCrop.height) / 2,
      0,
      Math.PI * 2,
    );
    croppedCtx.clip();
  }

  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    croppedCanvas.toBlob(
      (fileBlob) => {
        if (!fileBlob) {
          reject(new Error("Unable to crop image"));
          return;
        }
        resolve(fileBlob);
      },
      "image/jpeg",
      0.95,
    );
  });

  return new File([blob], fileName, { type: "image/jpeg" });
}

export function ImageUploadCropper({
  value,
  onChange,
  aspect = 1,
  title,
  description,
  disabled = false,
  className,
  previewClassName,
  emptyClassName,
  previewShape = "rect",
  error,
  accept = {
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/webp": [".webp"],
  },
  cropShape = "rect",
  maxZoom = 3,
  minZoom = 1,
  outputFileName = "image.jpg",
}: ImageUploadCropperProps) {
  const { t } = useTranslation();
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [internalError, setInternalError] = useState<string | null>(null);

  const displayError = error || internalError;
  const hasPreview = !!previewUrl;
  const displayTitle = title ?? t("imageUploadCropper.defaultTitle");
  const displayDescription = description ?? t("imageUploadCropper.defaultDescription");
  const supportedFormats = useMemo(
    () => Object.keys(accept).map((format) => format.replace("image/", "").toUpperCase()),
    [accept],
  );

  useEffect(() => {
    let objectUrl: string | null = null;

    if (value instanceof File) {
      objectUrl = URL.createObjectURL(value);
      setPreviewUrl(objectUrl);
    } else if (typeof value === "string" && value.trim()) {
      setPreviewUrl(value);
    } else {
      setPreviewUrl(null);
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [value]);

  useEffect(() => {
    return () => {
      if (sourceUrl) {
        URL.revokeObjectURL(sourceUrl);
      }
    };
  }, [sourceUrl]);

  const resetCrop = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
  };

  const openCropper = (file: File) => {
    if (!file.type.startsWith("image/")) {
      setInternalError(t("imageUploadCropper.invalidFile"));
      return;
    }

    setInternalError(null);
    const url = URL.createObjectURL(file);
    setSourceUrl(url);
    resetCrop();
    setIsCropOpen(true);
  };

  const onDrop = (acceptedFiles: File[]) => {
    if (disabled) return;
    const file = acceptedFiles[0];
    if (file) openCropper(file);
  };

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } =
    useDropzone({
      accept,
      multiple: false,
      disabled,
      noClick: hasPreview,
      noKeyboard: hasPreview,
      onDrop,
    });

  const handleCropComplete = (_croppedArea: Area, croppedAreaPixelsValue: Area) => {
    setCroppedAreaPixels(croppedAreaPixelsValue);
  };

  const handleApplyCrop = async () => {
    if (!sourceUrl || !croppedAreaPixels) return;

    const file = await getCroppedImg(
      sourceUrl,
      croppedAreaPixels,
      rotation,
      cropShape,
      outputFileName,
    );
    onChange(file);
    setIsCropOpen(false);
    setSourceUrl(null);
    resetCrop();
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setInternalError(null);
    setIsCropOpen(false);
    setSourceUrl(null);
    resetCrop();
  };

  const helperText = useMemo(() => {
    if (aspect === 1) return t("imageUploadCropper.squareCrop");
    return t("imageUploadCropper.cropRatio", { ratio: aspect.toFixed(2) });
  }, [aspect, t]);

  return (
    <div className={cn("w-full", className)}>
      <div
        {...getRootProps()}
        className={cn(
          "group relative overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-gradient-to-br from-white via-emerald-50/10 to-slate-50 p-4 text-center transition-all duration-200 hover:border-emerald-400 hover:shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950",
          disabled && "cursor-not-allowed opacity-60",
          !disabled && !hasPreview && "cursor-pointer",
          isDragActive && "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20",
          isDragAccept && "border-emerald-500",
          isDragReject && "border-red-500 bg-red-50/60 dark:bg-red-900/20",
          displayError ? "border-red-500" : "border-gray-300 dark:border-gray-600",
        )}
        onClick={() => {
          if (!disabled && !hasPreview) open();
        }}
      >
        <input {...getInputProps()} />

        {hasPreview ? (
          <div className={cn("flex flex-col items-center gap-4", previewClassName)}>
            <div className="flex w-full items-center justify-between px-1">
              <div className="text-left">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {displayTitle}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t("imageUploadCropper.readyToCrop")}
                </p>
              </div>
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                {helperText}
              </span>
            </div>
            <div
              className={cn(
                "relative overflow-hidden border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900",
                previewShape === "round"
                  ? "h-40 w-40 rounded-full"
                  : "w-full max-w-[520px] rounded-xl",
              )}
            >
              <img
                src={previewUrl || undefined}
                alt="Upload preview"
                className={cn("h-full w-full object-cover", previewShape === "rect" && "h-56")}
              />
              {previewShape === "rect" && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-4 py-3 text-left">
                  <p className="text-xs font-medium text-white/90">{displayDescription}</p>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  open();
                }}
                disabled={disabled}
              >
                <Upload className="h-4 w-4" />
                {t("imageUploadCropper.replace")}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRemove}
                disabled={disabled}
              >
                <X className="h-4 w-4" />
                {t("imageUploadCropper.remove")}
              </Button>
            </div>
          </div>
        ) : (
          <div
            className={cn(
              "flex min-h-[220px] flex-col items-center justify-center gap-3 py-6",
              emptyClassName,
            )}
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600 shadow-sm ring-1 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:ring-emerald-800/60">
              <Plus className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
                {displayTitle}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">{displayDescription}</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {helperText}
              </span>
              {supportedFormats.map((format) => (
                <span
                  key={format}
                  className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-500 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:ring-slate-700"
                >
                  {format}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {displayError && <p className="mt-2 text-sm text-red-500">{displayError}</p>}

      <Dialog
        open={isCropOpen}
        onOpenChange={(openState) => {
          setIsCropOpen(openState);
          if (!openState) {
            setSourceUrl(null);
            resetCrop();
          }
        }}
      >
        <DialogContent className="max-w-4xl z-[1301]">
          <DialogHeader>
            <DialogTitle>{t("imageUploadCropper.cropTitle")}</DialogTitle>
            <DialogDescription>{t("imageUploadCropper.cropDescription")}</DialogDescription>
          </DialogHeader>

          <div className="relative h-[60vh] w-full overflow-hidden rounded-lg bg-black">
            {sourceUrl && (
              <Cropper
                image={sourceUrl}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspect}
                cropShape={cropShape}
                minZoom={minZoom}
                maxZoom={maxZoom}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={handleCropComplete}
              />
            )}
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <ZoomOut className="h-4 w-4 text-gray-500" />
              <input
                type="range"
                min={minZoom}
                max={maxZoom}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1"
              />
              <ZoomIn className="h-4 w-4 text-gray-500" />
            </div>

            <div className="flex items-center gap-3">
              <RotateCcw className="h-4 w-4 text-gray-500" />
              <input
                type="range"
                min={0}
                max={360}
                step={1}
                value={rotation}
                onChange={(e) => setRotation(Number(e.target.value))}
                className="flex-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsCropOpen(false);
                setSourceUrl(null);
                resetCrop();
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleApplyCrop}
              disabled={!croppedAreaPixels}
            >
              {t("imageUploadCropper.applyCrop")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
